import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Switch } from '@/common/ui/switch'
import { Textarea } from '@/common/ui/textarea'
import { Button } from '@/common/ui/button'
import { FormProvider } from 'react-hook-form'
import useMutationUpdateGameTypeSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGameTypeSetting'
import { GetGameTypeSettingDataResponse } from '@/modules/client-setting/type'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import UploadImage from '@/common/components/UploadImage'
import useMutationCreateBannerGameFileUrl from '@/modules/operator/api/hooks/useMutationCreateBannerGameFileUrl'

const gameTypeSettingSchema = z.object({
	imageUrl: z.string(),
	gameCount: z
		.number({
			invalid_type_error: 'จำนวนเกมที่จะโชว์บนหน้า Home ต้องไม่ต่ำกว่า 0',
			required_error: 'จำนวนเกมที่จะโชว์บนหน้า Home ต้องไม่ต่ำกว่า 0',
		})
		.min(0, 'จำนวนเกมที่จะโชว์บนหน้า Home ต้องไม่ต่ำกว่า 0'),
	order: z.number().min(0, 'ลำดับการโชว์ต้องมากกว่าหรือเท่ากับ 0'),
	description: z.string(),
	isActive: z.boolean(),
})

type GameTypeSettingValues = z.infer<typeof gameTypeSettingSchema>

interface GameTypeSettingBoxProps {
	data: GetGameTypeSettingDataResponse
}

export default function GameTypeSettingBox({ data }: GameTypeSettingBoxProps) {
	const { mutate: updateGameType } = useMutationUpdateGameTypeSetting()

	const form = useForm<GameTypeSettingValues>({
		resolver: zodResolver(gameTypeSettingSchema),
		defaultValues: {
			imageUrl: data.imageUrl,
			gameCount: data.gameCount,
			order: data.order,
			description: data.description,
			isActive: data.isActive,
		},
	})

	const handleSubmit = (values: GameTypeSettingValues) => {
		try {
			updateGameType(
				{
					id: data.id,
					payload: values,
				},
				{
					onSuccess: () => {
						toast.success('บันทึกการเปลี่ยนแปลงสำเร็จ')
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const { mutateAsync: getBannerGameFileUrl } =
		useMutationCreateBannerGameFileUrl()

	return (
		<Card className="w-full">
			<CardHeader>
				<CardTitle>เกมประเภท {data.type}</CardTitle>
			</CardHeader>
			<CardContent>
				<FormProvider {...form}>
					<form
						onSubmit={form.handleSubmit(handleSubmit)}
						className="space-y-4"
					>
						<FormField
							control={form.control}
							name="gameCount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>จำนวนเกมที่จะโชว์บนหน้า Home</FormLabel>
									<FormControl>
										<Input
											type="number"
											{...field}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="order"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ลำดับการโชว์</FormLabel>
									<FormControl>
										<Input
											type="number"
											{...field}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>คำอธิบาย</FormLabel>
									<FormControl>
										<Textarea {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="isActive"
							render={({ field }) => (
								<FormItem className="flex items-center justify-between">
									<FormLabel>สถานะ</FormLabel>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="imageUrl"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										รูปภาพแบนเนอร์{' '}
										<span className="text-amber-600 dark:text-amber-400">
											(สัดส่วน 500:250)
										</span>
									</FormLabel>
									<FormControl>
										<UploadImage
											onImageUploaded={(fileUrl) => field.onChange(fileUrl)}
											defaultValue={field.value}
											getFileUrl={({ payload }) =>
												getBannerGameFileUrl({
													payload: { fileExtension: payload.fileExtension },
												})
											}
											aspectRatio="500/250"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button type="submit" className="w-full">
							บันทึกการเปลี่ยนแปลง
						</Button>
					</form>
				</FormProvider>
			</CardContent>
		</Card>
	)
}
